import React, { useState } from "react";
import { Alert, Icon, Spinner, Tabs } from "../../../design-system";
import { PREVIEWABLE_MIME_TYPES } from "../../../helpers/documents_helper";
import {
  EVENT_ICON,
  EVENT_LABEL,
  EVENT_TONE,
  STATUS_LABEL,
  VISIBILITY_LABEL,
} from "../constants";
import {
  categoryDisplayName,
  formatBytes,
  formatDate,
  formatRelativeTime,
  fullName,
  getExpiryStatus,
} from "../helpers";

export function DocumentDetail({
  doc,
  employee,
  previewUrl,
  previewLoading,
  previewError,
  events,
  eventsLoading,
}) {
  const previewable = PREVIEWABLE_MIME_TYPES.has(doc.mime_type);
  const [tab, setTab] = useState("preview");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Tabs
        tabs={[
          { id: "preview", label: "Previsualización" },
          { id: "file", label: "Archivo" },
          { id: "history", label: "Historial", count: events?.length || undefined },
        ]}
        value={tab}
        onChange={setTab}
      />

      {tab === "preview" && (
        <>
          <DocumentPreview
            doc={doc}
            previewable={previewable}
            previewUrl={previewUrl}
            loading={previewLoading}
            error={previewError}
          />
          <Section title="Información">
            <Meta label="Título" value={doc.title} />
            <Meta label="Categoría" value={categoryDisplayName(doc.category)} />
            <Meta label="Visibilidad" value={VISIBILITY_LABEL[doc.visibility] || doc.visibility} />
            <Meta label="Estado" value={STATUS_LABEL[doc.status] || doc.status} />
            {doc.expires_at && (
              <Meta
                label="Caducidad"
                value={<ExpiryValue iso={doc.expires_at} />}
              />
            )}
            {doc.description && <Meta label="Descripción" value={doc.description} />}
          </Section>
        </>
      )}

      {tab === "file" && (
        <Section title="Archivo">
          <Meta label="Nombre original" value={doc.original_filename} />
          <Meta label="Tipo MIME" value={doc.mime_type} />
          <Meta label="Tamaño" value={formatBytes(doc.size_bytes)} />
          {doc.checksum_sha256 && <Meta label="SHA-256" value={`${doc.checksum_sha256.slice(0, 16)}…`} />}
        </Section>
      )}

      {tab === "history" && (
        <>
          <Section title="Trazabilidad">
            {employee && doc.employee_profile_id && <Meta label="Empleado" value={fullName(employee)} />}
            {doc.published_at && <Meta label="Publicado" value={formatDate(doc.published_at)} />}
            {doc.archived_at && <Meta label="Archivado" value={formatDate(doc.archived_at)} />}
            {!doc.published_at && !doc.archived_at && !(employee && doc.employee_profile_id) && (
              <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>Sin metadatos extra.</div>
            )}
          </Section>

          <Section title="Historial">
            <DocumentTimeline events={events} loading={eventsLoading} />
          </Section>

          <Alert tone="info">
            Cada apertura, descarga y modificación queda registrada con el autor y la fecha.
          </Alert>
        </>
      )}
    </div>
  );
}

function DocumentPreview({ doc, previewable, previewUrl, loading, error }) {
  const containerStyle = {
    border: "1px solid var(--kr-border-subtle)",
    borderRadius: 8,
    background: "var(--kr-neutral-50)",
    minHeight: 280,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  };

  if (!previewable) {
    return (
      <div style={{ ...containerStyle, padding: 24, flexDirection: "column", gap: 8, textAlign: "center" }}>
        <Icon name="FileX" size={32} style={{ color: "var(--kr-fg-muted)" }} />
        <div style={{ fontSize: 13, color: "var(--kr-fg-strong)", fontWeight: 500 }}>
          Sin previsualización para este formato
        </div>
        <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>
          Descarga el archivo para abrirlo en tu equipo.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={containerStyle}>
        <Spinner size={20} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...containerStyle, padding: 24, flexDirection: "column", gap: 6 }}>
        <Icon name="AlertTriangle" size={24} style={{ color: "var(--kr-danger-700)" }} />
        <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", textAlign: "center" }}>{error}</div>
      </div>
    );
  }

  if (!previewUrl) {
    return <div style={containerStyle}>—</div>;
  }

  if (doc.mime_type === "application/pdf") {
    return (
      <iframe
        title={`Preview ${doc.title}`}
        src={previewUrl}
        style={{
          width: "100%",
          height: 480,
          border: "1px solid var(--kr-border-subtle)",
          borderRadius: 8,
          background: "#fff",
        }}
      />
    );
  }

  return (
    <div style={{ ...containerStyle, padding: 12 }}>
      <img
        src={previewUrl}
        alt={doc.title}
        style={{ maxWidth: "100%", maxHeight: 480, borderRadius: 4 }}
      />
    </div>
  );
}

function DocumentTimeline({ events, loading }) {
  if (loading) {
    return (
      <div style={{ padding: 12 }}>
        <Spinner size={14} />
      </div>
    );
  }
  if (!events || events.length === 0) {
    return (
      <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>
        Sin eventos registrados.
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {events.map((e) => (
        <DocumentTimelineRow key={e.id} event={e} />
      ))}
    </div>
  );
}

function DocumentTimelineRow({ event }) {
  const iconName = EVENT_ICON[event.event_type] || "Circle";
  const tone = EVENT_TONE[event.event_type] || "var(--kr-fg-muted)";
  const actorLabel = event.actor_name || event.actor_email || "Sistema";
  const action = EVENT_LABEL[event.event_type] || event.event_type;

  let detail = null;
  if (event.event_type === "renamed" && event.metadata?.changes) {
    const changes = event.metadata.changes;
    const keys = Object.keys(changes);
    detail = (
      <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>
        {keys.map((k) => `${k}`).join(", ")}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: 999,
          background: "var(--kr-bg-subtle)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: tone,
          flexShrink: 0,
          marginTop: 1,
        }}
      >
        <Icon name={iconName} size={12} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: "var(--kr-fg-strong)" }}>
          <strong>{actorLabel}</strong> {action}
        </div>
        <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>
          {formatRelativeTime(event.occurred_at)}
        </div>
        {detail}
      </div>
    </div>
  );
}

function ExpiryValue({ iso }) {
  const status = getExpiryStatus(iso);
  const date = formatDate(iso);
  const color =
    status === "expired"
      ? "var(--kr-danger-700)"
      : status === "soon"
        ? "var(--kr-warning-700)"
        : "var(--kr-fg-strong)";
  const label =
    status === "expired" ? `${date} (vencido)`
    : status === "soon" ? `${date} (próximo)`
    : date;
  return (
    <span style={{ color, fontFamily: "var(--kr-font-mono)", fontWeight: 500 }}>
      {label}
    </span>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: "var(--kr-fg-muted)",
          fontWeight: 600,
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "4px 0",
        fontSize: 12,
        gap: 12,
      }}
    >
      <span style={{ color: "var(--kr-fg-muted)" }}>{label}</span>
      <span
        style={{
          color: "var(--kr-fg-strong)",
          fontWeight: 500,
          textAlign: "right",
          wordBreak: "break-word",
        }}
      >
        {value}
      </span>
    </div>
  );
}
